import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useState,
} from "react";

type Drafts = Record<string, unknown>;

interface AdminDrafts {
  drafts: Drafts;
  setDraft: <Value>(name: string, update: SetStateAction<Value>, initial: Value) => void;
}

const AdminDraftsContext = createContext<AdminDrafts | null>(null);

// Holds what the admin entered in each Find search, and what each search asked, for as long as the
// admin is open, so that choosing another tab, or opening another screen, and coming back finds them
// (FIND-R17). It is not API data: the results stay in Redux.
export const AdminDraftsProvider = ({ children }: { children: ReactNode }) => {
  const [drafts, setDrafts] = useState<Drafts>({});

  const setDraft = useCallback(
    <Value,>(name: string, update: SetStateAction<Value>, initial: Value) =>
      setDrafts((previous) => {
        const current = (name in previous ? previous[name] : initial) as Value;
        const next =
          typeof update === "function" ? (update as (value: Value) => Value)(current) : update;

        return { ...previous, [name]: next };
      }),
    []
  );

  return (
    <AdminDraftsContext.Provider value={{ drafts, setDraft }}>
      {children}
    </AdminDraftsContext.Provider>
  );
};

// A piece of a search's state that outlives its tab and its screen: `useState` for a search shown
// inside the admin, and plain `useState` for one shown alone.
export const useAdminDraft = <Value,>(
  name: string,
  initial: Value
): [Value, Dispatch<SetStateAction<Value>>] => {
  const ownState = useState(initial);
  const shared = useContext(AdminDraftsContext);
  const setDraft = shared?.setDraft;

  const setShared = useCallback(
    (update: SetStateAction<Value>) => setDraft?.(name, update, initial),
    // `initial` is the constant the search starts from.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setDraft, name]
  );

  if (!shared) return ownState;

  const value = (name in shared.drafts ? shared.drafts[name] : initial) as Value;

  return [value, setShared];
};
